//Gallery/VisitorPostingsMobile.tsx

import React, { useEffect, useState, useRef, } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../../../shared/api/axios';
import {PostSection} from "./GalleryUIMobile";
import type {MediaEntry} from "./GalleryUIMobile";
import { useTranslation } from 'react-i18next';
import { useLikes, likeKeyFromRow } from '../../../shared/contexts/LikesContext';
import { FaBars } from 'react-icons/fa';
import { useAuth } from '../../../shared/contexts/AuthContext';
import type { Post as BasePost } from "./GalleryUIMobile";

export type Post = BasePost & {
  likeKey: string;
  type: 'media';
};

// Api base URL normalization
const IMAGE_CDN = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL as string;

if (!IMAGE_CDN) {
  throw new Error("REACT_APP_PUBLIC_IMAGE_BASE_URL is not defined");
}

const toImageUrl = (p?: string): string => {
  if (!p) return "";
  if (/^(https?:|data:)/i.test(p)) return p;
  return `${IMAGE_CDN.replace(/\/$/, "")}/${p.replace(/^\/+/, "")}`;
};
  
const formatMediaFlexible = (p: any): MediaEntry[] => {
  const list = p?.media || p?.attachments || p?.files || [];
  if (!Array.isArray(list)) return [];

  return list
    .map((m: any) => {
      // VIDEO — new pipeline
      if (m?.kind === 'video' || m?.isHls || m?.videoId || m?.hlsUrl || m?.dashUrl) {
        return {
          kind: 'video',
          isHls: !!m?.isHls || !!m?.videoId || !!m?.hlsUrl,
          videoId: m?.videoId ?? null,
          hlsUrl: m?.hlsUrl ?? null,
          dashUrl: m?.dashUrl ?? null,
          posterUrl: m?.posterUrl ?? null,
          musicId: m?.musicId ?? null,
          musicTitle: m?.musicTitle ?? null,
          musicArtist: m?.musicArtist ?? null,
        };
      }

      // IMAGE — string
      if (typeof m === 'string') {
        return {
          kind: 'image',
          url: toImageUrl(m),
          musicId: null,
          musicTitle: null,
          musicArtist: null,
        };
      }

      // IMAGE — object
      if (m?.url || m?.path || m?.src) {
        return {
          kind: 'image',
          url: toImageUrl(m?.url || m?.path || m?.src),
          musicId: m?.musicId ?? null,
          musicTitle: m?.musicTitle ?? null,
          musicArtist: m?.musicArtist ?? null,
        };
      }

      return null;
    })
    .filter(Boolean) as MediaEntry[];
};


const normalizeMe = (u: any) => ({
  profilename: u?.profilename || u?.username || 'User',
  location: u?.location || '',
  bio: u?.bio || '',
  picture: u?.picture ? toImageUrl(u.picture) : '',
  email: u?.email || '',
});



// Limiting Memory
const WINDOW_BEFORE = 1;
const WINDOW_AFTER = 2;
const MAX_POSTS_IN_MEMORY = 60;
const LIMIT = 20;



const VisitorPostingsMobile = () => {
   const { userId } = useParams<{ userId: string }>();
   const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [posts, setPosts] = useState<Post[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [visitor, setVisitor] = useState<{profilename:string; location:string; bio:string; picture:string; email:string;} | null>(null);
  const [globalMuted, setGlobalMuted] = useState(true);

  //Page behaviour
  const [pageIndex, setPageIndex] = useState(0);
  const feedRef = useRef<HTMLDivElement | null>(null);

  const [isFetching, setIsFetching] = useState(false);

  const [viewRestricted, setViewRestricted] = useState(false); // 👈 NEW

  const loc = useLocation();

  //Loading
  const isInitialLoading =
  isFetching &&
  posts.length === 0 &&
  !viewRestricted &&
  visitor === null;

const visitorName = visitor?.profilename ?? t("visitorPostings.restricted.name");

// hold the post id we want to focus
const [focusPostId, setFocusPostId] = useState<string | null>(null);

// map of postId -> element
const postRefs = useRef<Record<string, HTMLDivElement | null>>({});

//Only one Video at a time
useEffect(() => {
  const onPlay = (e: Event) => {
    const el = e.target as HTMLVideoElement | null;
    if (!el || el.tagName !== 'VIDEO') return;

    // Pause every other <video> on the page
    document.querySelectorAll('video').forEach((v) => {
      if (v !== el) {
        try { v.pause(); } catch {}
      }
    });
  };

  // Use capture phase to catch plays from custom players too
  document.addEventListener('play', onPlay, true);
  return () => document.removeEventListener('play', onPlay, true);
}, []);

const handleMenuClick = () => {
    navigate('/menu');  // Navigate to the menu page
  };

const handleOpenMusic = async (entry: MediaEntry) => {
  // 1️⃣ If backend already gave us a musicId, just go directly
  if (entry.musicId) {
    navigate(`/musicdetails/${entry.musicId}`);
    return;
  }

  // 2️⃣ Fallback: search by either title OR artist (whichever exists),
  //    instead of "title + artist" concatenation
  const q = (entry.musicTitle || entry.musicArtist || '').trim();
  if (!q) {
    console.warn('[VisitorPostings.handleOpenMusic] no title/artist on entry', entry);
    return;
  }

  try {
    const res = await api.get('/music', { params: { q } });
    const list = Array.isArray(res.data) ? res.data : (res.data?.tracks || []);
    if (!list.length) {
      console.warn('[VisitorPostings.handleOpenMusic] no match for', q);
      return;
    }

    navigate(`/musicdetails/${list[0]._id}`);
  } catch (e) {
    console.error('[VisitorPostings.handleOpenMusic] music lookup failed:', e);
  }
};

  const { likedIds, likeCounts, likePost, unlikePost, refreshLikes, } = useLikes();

  const fetchVisitorPosts = async (useOffset: number, append = false) => {
  if (!userId) return;
  try {
    setIsFetching(true);
    const res = await api.get(`/visitor-postings/${userId}`, {
      params: { offset: useOffset, limit: LIMIT },
    });

    const raw: any[] = res.data || [];

    const normalized: Post[] = raw.map((p: any) => {
      const likeKey = likeKeyFromRow(p);
    
      const likes =
        typeof p.likes === 'number'
          ? p.likes
          : typeof p.totalLikes === 'number'
          ? p.totalLikes
          : 0;
    
      return {
  ...p,
  _id: String(p._id),
  likeKey,
  type: 'media',
  captions: p.captions || [],
  media: formatMediaFlexible(p),
  likes,
  totalLikes: likes,
  likedByUser: likedIds.has(likeKey) || !!p.likedByUser,
  user: p.user
  ? {
      ...p.user,
      picture: p.user.picture ? toImageUrl(p.user.picture) : null,
    }
  : null,
};
    });
    
    
setPosts(prev => {
  const base = append ? prev : [];
  const merged = [...base, ...normalized];
  const byId = new Map(merged.map(p => [p._id, p]));
  return Array.from(byId.values()).slice(-MAX_POSTS_IN_MEMORY);
});

    setOffset(useOffset + LIMIT);
    setHasMore(raw.length === LIMIT);
    setViewRestricted(false); // ✅ successfully loaded → not restricted
  } catch (err: any) {
    console.error('❌ Failed to fetch visitor posts:', err);
    const status = err?.response?.status;
    if (status === 403 || status === 404) {
      // we are blocked or user/posts hidden → treat as "no posts" but normal layout
      setViewRestricted(true);
      setPosts([]);       // ensure empty
      setHasMore(false);  // no infinite loading
    }
  } finally {
    setIsFetching(false);
  }
};

const handleFeedScroll = (e: React.UIEvent<HTMLDivElement>) => {
  const el = e.currentTarget;
  const idx = Math.round((el.scrollTop || 0) / (el.clientHeight || 1));
  if (idx !== pageIndex) setPageIndex(idx);
};


//Refresh Likes
useEffect(() => {
  // When entering visitor postings, sync global likes from backend
  refreshLikes();
}, [refreshLikes]);

  
  // Activate Profile Card
  useEffect(() => {
    (async () => {
      try {
        if (!userId) return;
        const res = await api.get(`/users/${userId}`); // <-- adjust if your endpoint differs
        setVisitor(normalizeMe(res.data));
        setViewRestricted(false);
      } catch (e: any) {
        console.warn('Failed to load visitor profile', e);
        const status = e?.response?.status;
        if (status === 403 || status === 404) {
          // blocked / hidden / deleted: keep a generic card with ghost avatar
          setVisitor({
            profilename: t("visitorPostings.restricted.name") || "User",
            location: "",
            bio: t("visitorPostings.restricted.bio") || "",
            picture: "",
            email: "",
          });
          setViewRestricted(true);
        } else {
          setVisitor(null);
        }
      }
    })();
  }, [userId, t]);
  
  
 useEffect(() => {
  if (!posts.length) return;

  setPosts(prev =>
    prev.map(p => {
      const count = likeCounts.get(p.likeKey);

      return {
        ...p,
        likedByUser: likedIds.has(p.likeKey),
        likes:
          count != null
            ? Math.max(count, p.likes ?? 0) // ← critical (Gallery logic)
            : p.likes,
        totalLikes:
          count != null
            ? Math.max(count, p.totalLikes ?? p.likes ?? 0)
            : p.totalLikes,
      };
    })
  );
}, [likedIds, likeCounts, posts.length]);

    
  useEffect(() => {
    if (!userId) return;

    setPosts([]);
    setOffset(0);
    setHasMore(true);

    fetchVisitorPosts(0, false); // initial page with explicit offset 0
    setPageIndex(0);

  }, [userId]);


useEffect(() => {
  // pageIndex 0 = profile card
  const postIndex = pageIndex - 1;

  if (postIndex >= posts.length - 2 && hasMore && !isFetching) {
    fetchVisitorPosts(offset, true);
  }
}, [pageIndex, posts.length, hasMore, isFetching, offset]);


  //Loading
  useEffect(() => {
  const styleId = 'spin-keyframes';

  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.innerHTML = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;

  document.head.appendChild(style);
  return () => style.remove();
}, []);

  
  useEffect(() => {
    const fromHash = loc.hash?.startsWith('#post-') ? loc.hash.slice('#post-'.length) : null;
    const fromQuery = new URLSearchParams(loc.search).get('post');
    const fromState = (loc.state as any)?.focusPostId || null;
  
    setFocusPostId(fromHash || fromQuery || fromState || null);
  }, [loc]);

  useEffect(() => {
    if (!focusPostId) return;
    const el = postRefs.current[focusPostId];
    if (!el) return;
  
    // smooth scroll to the post
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  
    // pulse highlight
    el.animate(
      [
        { boxShadow: '0 0 0 0 rgba(37,99,235,0.0)' },
        { boxShadow: '0 0 0 6px rgba(37,99,235,0.35)' },
        { boxShadow: '0 0 0 0 rgba(37,99,235,0.0)' },
      ],
      { duration: 1200, easing: 'ease-in-out' }
    );
  }, [focusPostId, posts.length]); // re-run when posts change

  
const handleLike = async (likeKey: string) => {
  const target = String(likeKey);

  try {
    const newLikes = await likePost(target);

    setPosts(prev =>
      prev.map(p =>
        p.likeKey === target
          ? {
              ...p,
              likedByUser: true,
              ...(newLikes !== undefined
                ? { likes: newLikes, totalLikes: newLikes }
                : {}),
            }
          : p
      )
    );
  } catch (err) {
    console.error('❌ Failed to like post:', err);
  }
};

const handleUnlike = async (likeKey: string) => {
  const target = String(likeKey);

  try {
    const newLikes = await unlikePost(target);

    setPosts(prev =>
      prev.map(p =>
        p.likeKey === target
          ? {
              ...p,
              likedByUser: false,
              ...(newLikes !== undefined
                ? { likes: newLikes, totalLikes: newLikes }
                : {}),
            }
          : p
      )
    );
  } catch (err) {
    console.error('❌ Failed to unlike post:', err);
  }
};

  

return (
  <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', backgroundColor: '#000' }}>

    {/* FEED */}
 <div
  ref={feedRef}
  onScroll={handleFeedScroll}
  style={{
    ...feedContainerStyle,
    padding: 7,
    scrollSnapType: 'y mandatory',
    height: '100dvh',
  }}
>


      {/* ✅ PROFILE INFO CARD (TOP) */}
 {visitor && (
  <section
    style={{
      height: '100dvh',
      scrollSnapAlign: 'start',
      scrollSnapStop: 'always',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
      transform: 'translateY(clamp(-40px, -4vh, -80px))'

    }}
  >
    {/* Profile card */}
    <div
      style={{
        background: '#0f1115',
        border: '1px solid #1f2330',
        borderRadius: 16,
        padding: 18,
        width: '100%',
        maxWidth: 520,
        boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
      }}
    >
      <div style={{ display: 'grid', gap: 16 }}>
        
        {/* Avatar */}
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: 120,
              height: 120,
              margin: '0 auto',
              borderRadius: 16,
              overflow: 'hidden',
              border: '1px solid rgb(38,29,162)',
              background: 'rgb(38,29,162)',
            }}
          >
            {visitor.picture ? (
              <img
                src={visitor.picture}
                alt={t("visitorPostings.avatar.alt")}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 40,
                  color: '#fff',
                }}
              >
                👻
              </div>
            )}
          </div>
        </div>

        {/* Name */}
        <div>
          <div style={{ color: '#a6adbb', fontSize: 12 }}>
            {t("visitorPostings.header.name")}
          </div>
          <div style={{ color: '#e6e9f3', fontWeight: 600 }}>
            {visitor.profilename || '—'}
          </div>
        </div>

        {/* Location */}
        <div>
          <div style={{ color: '#a6adbb', fontSize: 12 }}>
            {t("visitorPostings.header.location")}
          </div>
          <div style={{ color: '#e6e9f3' }}>
            {visitor.location || '—'}
          </div>
        </div>

        {/* Bio */}
        <div>
          <div style={{ color: '#a6adbb', fontSize: 12 }}>
            {t("visitorPostings.header.about")}
          </div>
          <div style={{ color: '#e6e9f3', lineHeight: 1.35 }}>
            {visitor.bio || '—'}
          </div>
        </div>

      </div>
    </div>

    {/* Scroll hint */}
    <div
      style={{
        position: 'absolute',
        bottom: 28,
        left: 0,
        right: 0,
        textAlign: 'center',
        color: '#aaa',
        fontSize: 13,
        opacity: 0.9,
      }}
    >
      <div>
  {t("visitorPostings.scrollHint.viewPosts", { name: visitor.profilename,})}
      </div>
      <div style={{ fontSize: 26, marginTop: 4 }}>↓</div>
    </div>
  </section>
)}


      {/* ✅ POSTS FEED */}
 {isInitialLoading ? (
  <div
    style={{
      height: '70vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 14,
      color: '#bbb',
      fontSize: 16,
      letterSpacing: 0.4,
    }}
  >
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        border: '3px solid rgba(255,255,255,0.15)',
        borderTop: '3px solid #fff',
        animation: 'spin 0.9s linear infinite',
      }}
    />

    <div>
      {t("visitorPostings.loading.profile", { name: visitorName })}
    </div>
  </div>
  

) : posts.length > 0 ? (

posts.map((post, idx) => {
  const profileOffset = visitor ? 1 : 0;
  const visualIndex = idx + profileOffset;

  const isActive = pageIndex === visualIndex;
  const inWindow =
    visualIndex >= pageIndex - 1 &&
    visualIndex <= pageIndex + 1;

  const shouldRender =
    visualIndex >= pageIndex - WINDOW_BEFORE &&
    visualIndex <= pageIndex + WINDOW_AFTER;

  if (!shouldRender) {
    return (
      <section
        key={post._id}
        style={{
          minHeight: '100dvh',
          height: '100dvh',
          scrollSnapAlign: 'start',
          scrollSnapStop: 'always',
        }}
      />
    );
  }

  return (
    <section
      key={post._id}
      style={{
        minHeight: '100dvh',
        height: '100dvh',
        scrollSnapAlign: 'start',
        scrollSnapStop: 'always',
      }}
    >
      <PostSection
        post={post}
        isActive={isActive}
        inWindow={inWindow}
        navigate={navigate}
        globalMuted={globalMuted}
        onLike={() => handleLike(post.likeKey || post._id)}
        onUnlike={() => handleUnlike(post.likeKey || post._id)}
        onMusicClick={handleOpenMusic}
        currentUserId={user?._id}
      />
    </section>
  );
})

) : (
  <p style={{ color: '#aaa', padding: 16 }}>
  {viewRestricted
    ? t("visitorPostings.restricted.bio")
    : t("visitorPostings.empty.noPosts")}
</p>
)}


    </div>

    {/* ✅ MOBILE MENU BUTTON */}
    <div
      onClick={handleMenuClick}
      style={{
        position: 'absolute',
        top: 14,
        right: 17,
        padding: 10,
        cursor: 'pointer',
        zIndex: 10,
      }}
    >
      <FaBars style={{ color: '#bbb', fontSize: 19 }} />
    </div>

  </div>
);
};


const feedContainerStyle: React.CSSProperties = {
  flexGrow: 1,
  padding: '20px',
  overflowY: 'auto',  // This ensures the container is vertically scrollable
  height: 'calc(100vh - 50px)',  // Adjust to fit screen
  minHeight: '100%', 
  backgroundColor: '#000',
  border: 'none',
  touchAction: 'pan-y',  // Allows vertical scroll and swipe
  overscrollBehaviorY: 'contain',  // Prevents pull-to-refresh
  overscrollBehaviorX: 'none',
  overflowAnchor: 'none',  // Prevents scroll jump behavior
};  

export default VisitorPostingsMobile;